import { useState } from 'react';
import { Modal, Cell, Input, Textarea, Button, Checkbox } from '@telegram-apps/telegram-ui';
import { Villa } from '../types';
import { updateVilla, togglePin, normalizePhone, formatPhone } from '../api';

interface EditDrawerProps {
  villa: Villa;
  open: boolean;
  onClose: () => void;
  onChange: (villa: Villa) => void;
  onMoveToArchive: (id: string) => void;
  isArchived?: boolean;
}

export default function EditDrawer({
  villa,
  open,
  onClose,
  onChange,
  onMoveToArchive,
  isArchived = false,
}: EditDrawerProps) {
  const [phone, setPhone] = useState(villa.phone || '');
  const [comment, setComment] = useState(villa.comment || '');
  const [price, setPrice] = useState(villa.price ? String(villa.price) : '');
  const [phoneError, setPhoneError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      const updated = await updateVilla(villa.id, {
        phone: phone.trim() || null,
        comment: comment.trim() || null,
        price: price.trim() ? parseInt(price.trim()) : null,
      });
      onChange(updated);
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      setLoading(false);
    }
  };

  const handleTogglePin = async () => {
    try {
      const updated = await togglePin(villa.id, !villa.pinned);
      onChange(updated);
    } catch (err) {
      console.error('Pin error:', err);
    }
  };

  const handleArchive = async () => {
    try {
      const updated = await updateVilla(villa.id, { status: 'archived' });
      onMoveToArchive(villa.id);
      onClose();
    } catch (err) {
      console.error('Archive error:', err);
    }
  };

  const handleUnarchive = async () => {
    try {
      const updated = await updateVilla(villa.id, { status: 'new' });
      onChange(updated);
      onClose();
    } catch (err) {
      console.error('Unarchive error:', err);
    }
  };

  const handleClose = () => {
    setPhone(villa.phone || '');
    setComment(villa.comment || '');
    setPrice(villa.price ? String(villa.price) : '');
    setPhoneError('');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      header={`Редактировать: ${villa.title || new URL(villa.url).hostname}`}
    >
      <div className="edit-drawer__content">
        {/* Phone */}
        <div className="edit-drawer__field">
          <Input
            header="Телефон для WhatsApp"
            value={phone}
            onChange={e => {
              setPhone(e.target.value);
              if (e.target.value.trim() && normalizePhone(e.target.value).length < 12) {
                setPhoneError('Номер слишком короткий');
              } else {
                setPhoneError('');
              }
            }}
            placeholder="+62 812 345 6789"
            status={phoneError ? 'error' : 'default'}
          />
          {phone && (
            <div className="edit-drawer__hint">
              Отобразится как: {formatPhone(phone)}
            </div>
          )}
        </div>

        {/* Comment */}
        <div className="edit-drawer__field">
          <Textarea
            header="Комментарий"
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Заметки о вилле..."
            rows={3}
          />
        </div>

        {/* Price */}
        <div className="edit-drawer__field">
          <Input
            header="Цена (IDR)"
            type="number"
            value={price}
            onChange={e => setPrice(e.target.value)}
            placeholder="5000000"
          />
        </div>

        <div className="edit-drawer__separator" />

        {/* Pin toggle */}
        <Cell
          onClick={handleTogglePin}
          after={<Checkbox checked={villa.pinned} onChange={() => {}} />}
          multiline={false}
        >
          Запинить
        </Cell>

        <div className="edit-drawer__separator" />

        {/* Actions */}
        <div className="edit-drawer__actions">
          <Button
            stretched
            onClick={handleSave}
            loading={loading}
          >
            Сохранить
          </Button>

          {!isArchived ? (
            <Button
              mode="destructive"
              onClick={handleArchive}
              stretched
            >
              В архив
            </Button>
          ) : (
            <Button
              onClick={handleUnarchive}
              stretched
            >
              Вернуть из архива
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
